/* ═══════════════════════════════════════════════════════════════════════════
   Oznaka testnog okruzenja.

   Siroka je tacno koliko i natpis — nije traka preko celog ekrana. Stoji
   `position: fixed` na samom vrhu, pa NE pomera sadrzaj: `#main` je visok
   `calc(100% - 60px)`, tacno za visinu zaglavlja, i svaki stvarni red iznad bi
   gurnuo radnu povrsinu van ekrana.

   Posto na vrhu nema praznog mesta (levo hamburger, sredina logo, desno nalog),
   oznaka se moze UHVATITI I POMERATI LEVO-DESNO. Namerno samo po toj osi:
   smisao joj je da stoji na vrhu, a po visini nema gde da se skloni. Polozaj se
   pamti po pregledacu, pa se ne namesta ponovo pri svakom otvaranju.

   ZASTO SE OGRANICENJE RACUNA PRI ISCRTAVANJU, a ne upisuje u stanje:
   prvo je bilo tako da se pri promeni velicine prozora u stanje upise vec
   ogranicena vrednost. Posledica — ako se prozor suzi, oznaka se zalepi uz levu
   ivicu i TAMO OSTANE i kad se prozor opet rasiri, jer je zeljeni polozaj
   prepisan. Sada se cuva ono sto je korisnik izabrao, a granica se primenjuje
   tek pri iscrtavanju, pa se po povratku na siri prozor oznaka vraca na svoje.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jeTestnoOkruzenje } from './okruzenje';
import './OznakaOkruzenja.css';

const KLJUC = 'tms.oznakaOkruzenja.left';
const IVICA = 6;   // koliko najmanje ostaje do ivice ekrana

/* localStorage ume da pukne (privatni prozor, blokirani kolacici), a polozaj
   oznake nije podatak zbog kog aplikacija sme da padne. */
const procitajLevo = () => {
    try {
        const v = window.localStorage.getItem(KLJUC);
        if (v === null) return null;
        const broj = Number(v);
        return Number.isFinite(broj) ? broj : null;
    } catch {
        return null;
    }
};

const zapisiLevo = (v) => {
    try {
        window.localStorage.setItem(KLJUC, String(Math.round(v)));
    } catch {
        /* nema veze — oznaka ce sledeci put krenuti od podrazumevanog mesta */
    }
};

const OznakaOkruzenja = () => {
    const { t } = useTranslation();
    const elRef = useRef(null);
    const pomerajRef = useRef(null);                 // razmak od leve ivice do kursora dok traje vucenje

    const [levo, setLevo] = useState(procitajLevo);  // ZELJENO mesto, sirovo
    const [sirinaOznake, setSirinaOznake] = useState(0);
    const [sirinaProzora, setSirinaProzora] = useState(
        () => (typeof window === 'undefined' ? 0 : window.innerWidth)
    );
    const [vuce, setVuce] = useState(false);

    /* Sirina se meri posle iscrtavanja — zavisi od jezika, pa se prati i `t`. */
    useLayoutEffect(() => {
        if (elRef.current) setSirinaOznake(elRef.current.offsetWidth);
    }, [t]);

    useEffect(() => {
        const naPromenu = () => setSirinaProzora(window.innerWidth);
        window.addEventListener('resize', naPromenu);
        return () => window.removeEventListener('resize', naPromenu);
    }, []);

    if (!jeTestnoOkruzenje) return null;

    const ogranici = (x) => {
        const najvise = Math.max(IVICA, sirinaProzora - sirinaOznake - IVICA);
        return Math.min(Math.max(x, IVICA), najvise);
    };

    /* Prvi put, dok korisnik nije nista pomerao — na sredinu. */
    const zeljeno = levo === null ? (sirinaProzora - sirinaOznake) / 2 : levo;
    const prikazano = ogranici(zeljeno);

    /* Pointer events, ne mouse: isti kod pokriva mis i dodir na tabletu.
       setPointerCapture drzi dogadjaje na elementu i kad kursor izadje van njega,
       pa vucenje ne "pukne" kad se brzo prevuce preko zaglavlja. */
    const naPritisak = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        pomerajRef.current = e.clientX - elRef.current.getBoundingClientRect().left;
        setVuce(true);
        elRef.current.setPointerCapture?.(e.pointerId);
    };

    const naPomeranje = (e) => {
        if (pomerajRef.current === null) return;
        e.preventDefault();                           // bez ovoga se vuce izbor teksta
        setLevo(ogranici(e.clientX - pomerajRef.current));
    };

    const naOtpustanje = (e) => {
        if (pomerajRef.current === null) return;
        pomerajRef.current = null;
        setVuce(false);
        elRef.current?.releasePointerCapture?.(e.pointerId);
        zapisiLevo(ogranici(levo === null ? zeljeno : levo));
    };

    return (
        <div
            ref={elRef}
            className={`okruzenje-oznaka${vuce ? ' okruzenje-oznaka-vuce' : ''}`}
            style={{ left: prikazano }}
            onPointerDown={naPritisak}
            onPointerMove={naPomeranje}
            onPointerUp={naOtpustanje}
            onPointerCancel={naOtpustanje}
            title={t('common.testnoOkruzenjeOpis')}
            role="status"
        >
            <i className="pi pi-exclamation-triangle" />
            <span>{t('common.testnoOkruzenje')}</span>
        </div>
    );
};

export default OznakaOkruzenja;
